'use client'

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, Suspense } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { isReadOnly, isReviewerOnly } from '@/lib/roles'
import type { ChatAttachment, ChatContext } from '@/lib/ai/types'

// Types for chat messages
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  toolCalls?: ToolCall[]
  isLoading?: boolean
  /** C2 — structured cards rendered under an assistant message. */
  attachments?: ChatAttachment[]
  /** C2 — follow-up chips rendered under the last assistant message. */
  suggestedPrompts?: string[]
}

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

// Types for policies
export interface AIPolicy {
  id: string
  name: string
  description: string
  naturalLanguage: string
  dsl: PolicyDSL | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PolicyDSL {
  conditions: PolicyCondition[]
  actions: PolicyAction[]
  match_mode?: 'all' | 'any'
  stop_on_match?: boolean
}

export interface PolicyCondition {
  field: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'between' | 'in' | 'not_in' | 'matches' | 'is_null' | 'is_not_null'
  value: string | number | boolean | unknown[] | null
  field_path?: string
}

export interface PolicyAction {
  type: 'auto_approve' | 'auto_reject' | 'flag_review' | 'notify' | 'set_status' | 'set_field' | 'add_note' | 'add_tag' | 'escalate' | 'custom'
  value?: string | number | boolean | Record<string, unknown>
  params?: Record<string, unknown>
}

// Types for insights
export interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'recommendation'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  data?: Record<string, unknown>
  suggestedAction?: string
  createdAt: Date
}

// Context state type
interface AIContextState {
  // AI Manager state
  /** False for read-only roles (AP-Operator): launcher hidden, ⌘J inert. */
  aiEnabled: boolean
  isManagerOpen: boolean
  openManager: () => void
  closeManager: () => void
  toggleManager: () => void
  
  // Chat state
  chatHistory: ChatMessage[]
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  clearHistory: () => void
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  
  // Page context
  currentPageContext: string
  /** C3 — full page/selection context sent with every chat request. */
  chatContext: ChatContext
  /** Pages may publish extra selection data (e.g. selected explorer row ids). */
  setSelection: (selection: Record<string, unknown> | null) => void
  
  // Policies state
  policies: AIPolicy[]
  setPolicies: (policies: AIPolicy[]) => void
  addPolicy: (policy: AIPolicy) => void
  updatePolicy: (id: string, updates: Partial<AIPolicy>) => void
  removePolicy: (id: string) => void
  
  // Insights state
  insights: AIInsight[]
  setInsights: (insights: AIInsight[]) => void
  pendingInsightsCount: number
  markInsightAsRead: (id: string) => void
  
  // Suggestions
  hasPendingSuggestions: boolean
  setHasPendingSuggestions: (has: boolean) => void
}

const AIContext = createContext<AIContextState | undefined>(undefined)

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

const EMPTY_CONTEXT: ChatContext = { page: '/', invoice_id: null, queue: null, tab: null, highlight: null, selection: null }

/** Active workbench tab (local page state, not in the URL) — read from the tablist. */
function readActiveTabLabel(): string | null {
  if (typeof document === 'undefined') return null
  const el = document.querySelector('[role="tab"][aria-selected="true"]')
  const text = el?.textContent?.trim()
  return text ? text : null
}

/**
 * Derives route/query context. Lives in its own component so the
 * `useSearchParams()` call sits under a Suspense boundary (Next 15 build rule)
 * without forcing the whole provider tree to suspend.
 */
function RouteContextBridge({ onChange }: { onChange: (ctx: Omit<ChatContext, 'selection'>) => void }) {
  const pathname = usePathname() || '/'
  const params = useSearchParams()
  const [tabLabel, setTabLabel] = useState<string | null>(null)

  const isWorkbench = pathname.startsWith('/workbench')

  // Workbench tab is component state; observe aria-selected flips in the DOM.
  useEffect(() => {
    if (!isWorkbench || typeof MutationObserver === 'undefined') {
      setTabLabel(null)
      return
    }
    setTabLabel(readActiveTabLabel())
    const observer = new MutationObserver(() => setTabLabel(readActiveTabLabel()))
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-selected'], childList: true })
    return () => observer.disconnect()
  }, [isWorkbench, pathname])

  const rawInvoice = params.get('invoice') ?? params.get('id')
  const invoiceId = isWorkbench && rawInvoice && /^\d+$/.test(rawInvoice) ? Number(rawInvoice) : null
  const queue = isWorkbench ? params.get('queue') : null
  const explorerTab = pathname.startsWith('/invoices') ? params.get('status') ?? 'all' : null
  const dataTab = pathname.startsWith('/data-manager') ? params.get('tab') : null
  const highlight = pathname.startsWith('/ai/policies') ? params.get('highlight') : null
  const tab = isWorkbench ? tabLabel : explorerTab ?? dataTab

  useEffect(() => {
    onChange({ page: pathname, invoice_id: invoiceId, queue, tab, highlight })
  }, [onChange, pathname, invoiceId, queue, tab, highlight])

  return null
}

export function AIProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  // The copilot carries action tools, so a read-only operator never gets it,
  // and a reviewer-only session (plan 04 WP-REV-07) has no AI Manager at all.
  const aiEnabled = !isReadOnly(session?.roles) && !isReviewerOnly(session?.roles)
  const [routeContext, setRouteContext] = useState<Omit<ChatContext, 'selection'>>(EMPTY_CONTEXT)
  const [selection, setSelection] = useState<Record<string, unknown> | null>(null)

  // Selection is page-specific; drop it when the route changes.
  useEffect(() => {
    setSelection(null)
  }, [pathname])

  const chatContext = useMemo<ChatContext>(() => ({ ...routeContext, selection }), [routeContext, selection])
  
  // AI Manager state
  const [isManagerOpen, setIsManagerOpen] = useState(false)
  
  // Chat state
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)
  
  // Policies state
  const [policies, setPolicies] = useState<AIPolicy[]>([])
  
  // Insights state
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [readInsightIds, setReadInsightIds] = useState<Set<string>>(new Set())
  
  // Suggestions state
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState(false)
  
  // Derive page context from pathname
  const currentPageContext = pathname || '/'
  
  // Manager controls (no-ops when the role may not use the copilot)
  const openManager = useCallback(() => { if (aiEnabled) setIsManagerOpen(true) }, [aiEnabled])
  const closeManager = useCallback(() => setIsManagerOpen(false), [])
  const toggleManager = useCallback(() => { if (aiEnabled) setIsManagerOpen(prev => !prev) }, [aiEnabled])

  useEffect(() => {
    if (!aiEnabled) setIsManagerOpen(false)
  }, [aiEnabled])
  
  // Chat controls
  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    }
    
    // If this is a real assistant message (not loading), remove any existing loading messages first
    if (message.role === 'assistant' && !message.isLoading) {
      setChatHistory(prev => [...prev.filter(m => !m.isLoading), newMessage])
    } else {
      setChatHistory(prev => [...prev, newMessage])
    }
  }, [])
  
  const clearHistory = useCallback(() => {
    setChatHistory([])
  }, [])
  
  // Policy controls
  const addPolicy = useCallback((policy: AIPolicy) => {
    setPolicies(prev => [...prev, policy])
  }, [])
  
  const updatePolicy = useCallback((id: string, updates: Partial<AIPolicy>) => {
    setPolicies(prev => 
      prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p)
    )
  }, [])
  
  const removePolicy = useCallback((id: string) => {
    setPolicies(prev => prev.filter(p => p.id !== id))
  }, [])
  
  // Insights controls
  const pendingInsightsCount = insights.filter(i => !readInsightIds.has(i.id)).length
  
  const markInsightAsRead = useCallback((id: string) => {
    setReadInsightIds(prev => new Set([...prev, id]))
  }, [])
  
  // Keyboard shortcut for AI Manager (Cmd+J or Ctrl+J)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleManager()
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleManager])
  
  const value: AIContextState = {
    // Manager
    aiEnabled,
    isManagerOpen,
    openManager,
    closeManager,
    toggleManager,
    
    // Chat
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    
    // Page context
    currentPageContext,
    chatContext,
    setSelection,
    
    // Policies
    policies,
    setPolicies,
    addPolicy,
    updatePolicy,
    removePolicy,
    
    // Insights
    insights,
    setInsights,
    pendingInsightsCount,
    markInsightAsRead,
    
    // Suggestions
    hasPendingSuggestions,
    setHasPendingSuggestions,
  }
  
  return (
    <AIContext.Provider value={value}>
      <Suspense fallback={null}>
        <RouteContextBridge onChange={setRouteContext} />
      </Suspense>
      {children}
    </AIContext.Provider>
  )
}

